import React from 'react';
import { FiSearch, FiX, FiChevronLeft, FiChevronRight } from 'react-icons/fi';
import './marketplace-shared.css';

// ── Search bar ────────────────────────────────────────────────────────────────

interface GridSearchBarProps {
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
}

export const GridSearchBar: React.FC<GridSearchBarProps> = ({
  value,
  onChange,
  placeholder = 'Search...'
}) => (
  <div className='mk-search'>
    <FiSearch size={13} className='mk-search__icon' />
    <input
      type='text'
      className='mk-search__input'
      placeholder={placeholder}
      value={value}
      onChange={e => onChange(e.target.value)}
    />
    {value && (
      <button
        className='mk-search__clear'
        onClick={() => onChange('')}
        aria-label='Clear search'
      >
        <FiX size={12} />
      </button>
    )}
  </div>
);

// ── Pagination ────────────────────────────────────────────────────────────────

export function buildPageWindow(
  current: number,
  total: number
): (number | null)[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);

  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  const pages: (number | null)[] = [1];

  if (left > 2) pages.push(null);
  for (let p = left; p <= right; p++) pages.push(p);
  if (right < total - 1) pages.push(null);
  pages.push(total);

  return pages;
}

interface GridPaginationProps {
  currentPage: number;
  totalPages: number;
  totalCount: number;
  label: string;
  onPage: (page: number) => void;
}

export const GridPagination: React.FC<GridPaginationProps> = ({
  currentPage,
  totalPages,
  totalCount,
  label,
  onPage
}) => {
  if (totalPages <= 1) return null;

  const pageWindow = buildPageWindow(currentPage, totalPages);

  return (
    <div className='mk-pagination'>
      <div className='mk-pagination__info'>
        Page {currentPage} of {totalPages} · {totalCount} {label}
      </div>

      <button
        className='mk-pagination__btn'
        onClick={() => onPage(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label='Previous page'
      >
        <FiChevronLeft size={14} />
      </button>

      {pageWindow.map((page, i) =>
        page === null ? (
          <span key={`ellipsis-${i}`} className='mk-pagination__ellipsis'>
            …
          </span>
        ) : (
          <button
            key={page}
            className={`mk-pagination__btn${page === currentPage ? ' mk-pagination__btn--active' : ''}`}
            onClick={() => onPage(page)}
            disabled={page === currentPage}
          >
            {page}
          </button>
        )
      )}

      <button
        className='mk-pagination__btn'
        onClick={() => onPage(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label='Next page'
      >
        <FiChevronRight size={14} />
      </button>
    </div>
  );
};
